import { TextField, Grid, Autocomplete, FormControlLabel, MenuItem, FormControl, RadioGroup, FormLabel, Radio, Card, CardHeader, CardContent, Divider, Typography, Stack, Tooltip } from "@mui/material"
import { useState, forwardRef, useImperativeHandle, memo, useMemo } from "react"

import { useFormik } from "formik"
import * as Yup from "yup"
import { useStores } from "stores/root-store"
import { rootStore } from "stores/root-store"
import { deliveryCodes, ausStates } from "shared/constants/sale-order-constant"
import { GridContainer } from "components/common-styles"

const SaleOrderDelivery = memo(
  forwardRef(({ saleOrderInfo, onErpOrderChange, onDeliveryCountryChange }, ref) => {
    const { SaleOrderStore } = useStores()
    const isEditing = Boolean(SaleOrderStore.deliveryInfo)
    const [showCustomerInfo, setShowCustomerInfo] = useState(true)

    const formik = useFormik({
      enableReinitialize: true,
      initialValues: saleOrderInfo,
      validationSchema: Yup.object({
        ship_to_name: Yup.string().max(40).required("Delivery Name is required"),
        erp_order: Yup.string()
          .max(21)
          .required("Order Number is required")
          .test("shouldNotIncludeSpace", "Order Number should not include space", val => isEditing || !val || !val.includes(" ")),
        ship_to_country: Yup.string().required("Delivery Country is required"),
        warehouse: Yup.string().max(40).required("Warehouse is required"),
        ship_to_city: Yup.string().max(40).required("Delivery Suburb is required"),
        ship_to_state: Yup.string().max(40).required("Delivery State is required"),
        ship_to_address1: Yup.string().max(40).required("Delivery Address Line 1 is required"),
        ship_to_email_address: Yup.string().nullable().email("Must be a valid email").max(50),
        customer_email_address: Yup.string().nullable().email("Must be a valid email").max(50),
        ship_to_postal_code: Yup.string().max(10).required("Delivery Postal Code is required"),
        carrier: isEditing ? Yup.string().nullable() : Yup.string().required("Carrier is required"),
        carrier_service: isEditing ? Yup.string().nullable() : Yup.string().required("Carrier Service is required"),
      }),
      onSubmit: async values => {
        //console.log(values)
      },
    })

    useImperativeHandle(ref, () => ({
      async getFormDataIfValid() {
        const val = await formik.validateForm()
        await formik.submitForm()

        if (!val || Object.keys(val).length == 0) return formik.values
        return null
      },
    }))

    const handleErpOrderChange = event => {
      onErpOrderChange(event.target.value)
      formik.handleChange(event)
    }

    const handleDeliveryCountryChange = val => {
      onDeliveryCountryChange(val)
      formik.setFieldValue("ship_to_country", val)
    }

    const countries = useMemo(() => rootStore.getOptionsByFilter("COUNTRY"), [])

    // Carriers and their services come from the company-scoped CarrierService
    // API (rows of { CARRIER, SERVICE, DESCRIPTION }), so only carriers/services
    // available to this customer are offered. Group the services under each
    // carrier, preserving the order the API returned them in.
    // When the CarrierService API returns nothing, fall back to the global
    // CARRIER/SERVICE options from FilterCriteria, as before.
    const { availableCarriers, servicesByCarrier } = useMemo(() => {
      if (!rootStore.carrierService?.length) {
        const carriers = rootStore.getOptionsByFilter("CARRIER")
        const services = rootStore.getOptionsByFilter("SERVICE")
        const servicesByCarrier = {}
        carriers.forEach(carrier => {
          servicesByCarrier[carrier.value] = services
            .filter(x => x.identifier && x.identifier.includes(`FILTER_CARRIER_${carrier.value}`))
            .map(x => ({ value: x.value, label: x.value }))
        })
        const availableCarriers = carriers.map(x => ({ value: x.value, label: `${x.value}-${x.label}` }))
        return { availableCarriers, servicesByCarrier }
      }

      const servicesByCarrier = {}
      const carrierOrder = []
      ;(rootStore.carrierService || []).forEach(row => {
        const carrier = row.CARRIER
        if (!carrier) return
        if (!servicesByCarrier[carrier]) {
          servicesByCarrier[carrier] = []
          carrierOrder.push(carrier)
        }
        servicesByCarrier[carrier].push({ value: row.SERVICE, label: row.DESCRIPTION ? `${row.SERVICE} - ${row.DESCRIPTION}` : row.SERVICE })
      })
      const availableCarriers = carrierOrder.map(carrier => ({ value: carrier, label: carrier }))
      return { availableCarriers, servicesByCarrier }
    }, [rootStore.carrierService])

    const selectedCarrier = formik.values.carrier || ""
    const selectedService = formik.values.carrier_service || ""
    // Keep an already-saved carrier/service selectable when editing an order,
    // even if it is no longer in the available list, so the field is not
    // silently blanked.
    const carrierOptions =
      selectedCarrier && !availableCarriers.some(c => c.value === selectedCarrier) ? [...availableCarriers, { value: selectedCarrier, label: selectedCarrier }] : availableCarriers
    const carrierServices = servicesByCarrier[selectedCarrier] || []
    const serviceOptions = selectedService && !carrierServices.some(s => s.value === selectedService) ? [...carrierServices, { value: selectedService, label: selectedService }] : carrierServices

    return (
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <Card>
            <CardHeader title="Delivery Information" />
            <CardContent>
              <GridContainer container spacing={3}>
                <Grid item md={6} xs={12}>
                  <Tooltip placement="top" title="destination contact name">
                    <TextField
                      fullWidth
                      error={Boolean(formik.touched.ship_to_name && formik.errors.ship_to_name)}
                      helperText={formik.touched.ship_to_name && formik.errors.ship_to_name}
                      label="Delivery Name"
                      name="ship_to_name"
                      id="ship_to_name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      required
                      value={formik.values.ship_to_name}
                      variant="outlined"
                      inputProps={{ maxLength: 40 }}
                    />
                  </Tooltip>
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Shipping Method"
                    name="shipping_method"
                    id="shipping_method"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.shipping_method}
                    variant="outlined"
                    inputProps={{ maxLength: 25 }}
                  />
                </Grid>

                <Grid item md={6} xs={12}>
                  <Tooltip placement="top" title="destination company name">
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="ship_to"
                      id="ship_to"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.ship_to}
                      variant="outlined"
                      inputProps={{ maxLength: 25 }}
                    />
                  </Tooltip>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Tooltip placement="top" title="unique sales order reference">
                    <TextField
                      fullWidth
                      required
                      error={Boolean(formik.touched.erp_order && formik.errors.erp_order)}
                      helperText={formik.touched.erp_order && formik.errors.erp_order}
                      inputProps={{ maxLength: 21 }}
                      label="Order Number"
                      name="erp_order"
                      id="erp_order"
                      onBlur={formik.handleBlur}
                      onChange={handleErpOrderChange}
                      value={formik.values.erp_order}
                      variant="outlined"
                      InputProps={{
                        readOnly: isEditing,
                      }}
                    />
                  </Tooltip>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Tooltip placement="top" title="origin warehouse the order will be shipped from">
                    <TextField
                      fullWidth
                      error={Boolean(formik.touched.warehouse && formik.errors.warehouse)}
                      helperText={formik.touched.warehouse && formik.errors.warehouse}
                      required
                      select={!isEditing}
                      label="Warehouse"
                      name="warehouse"
                      id="warehouse"
                      onChange={formik.handleChange}
                      value={formik.values.warehouse}
                      variant="outlined"
                      InputProps={{
                        readOnly: isEditing,
                      }}
                    >
                      {rootStore.getOptionsByFilter("WAREHOUSE").map(option => (
                        <MenuItem id={`warehouse-${option.value}`} key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Tooltip>
                </Grid>

                <Grid item md={3} xs={12}>
                  <Tooltip placement="top" title="freight carrier">
                    <TextField
                      id="carrier"
                      fullWidth
                      error={Boolean(formik.touched.carrier && formik.errors.carrier)}
                      helperText={formik.touched.carrier && formik.errors.carrier}
                      required={!isEditing}
                      select
                      label="Carrier"
                      name="carrier"
                      onChange={event => {
                        // Changing the carrier invalidates the previously chosen
                        // service, so clear it — the user must pick a service
                        // that belongs to the newly selected carrier.
                        formik.setFieldValue("carrier", event.target.value)
                        formik.setFieldValue("carrier_service", "")
                      }}
                      value={formik.values.carrier || ""}
                      variant="outlined"
                    >
                      {carrierOptions.map(option => (
                        <MenuItem id={`carrier-${option.value}`} key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Tooltip>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Tooltip placement="top" title="freight carriers service code">
                    <TextField
                      fullWidth
                      error={Boolean(formik.touched.carrier_service && formik.errors.carrier_service)}
                      helperText={formik.touched.carrier_service && formik.errors.carrier_service}
                      required={!isEditing}
                      disabled={!formik.values["carrier"]}
                      select
                      label="Service"
                      name="carrier_service"
                      id="carrier_service"
                      onChange={formik.handleChange}
                      value={formik.values.carrier_service || ""}
                      variant="outlined"
                    >
                      {serviceOptions.map(option => (
                        <MenuItem id={`carrier_service-${option.value}`} key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Tooltip>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Tooltip placement="top" title="special delivery instructions to be passed onto the freight carrier">
                    <TextField
                      fullWidth
                      select
                      label="Delivery Code"
                      name="delivery_code"
                      id="delivery_code"
                      onChange={formik.handleChange}
                      value={formik.values.delivery_code || ""}
                      variant="outlined"
                    >
                      {deliveryCodes.map(option => (
                        <MenuItem id={`delivery_code-${option}`} key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Tooltip>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Tooltip placement="top" title="pre-defined brand code for shipment">
                    <TextField
                      fullWidth
                      inputProps={{ maxLength: 10 }}
                      label="Brand Code"
                      name="brand_code"
                      id="brand_code"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.brand_code}
                      variant="outlined"
                    />
                  </Tooltip>
                </Grid>
              </GridContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader title="Shipping information" />
            <CardContent>
              <GridContainer container spacing={3}>
                <Grid item md={6} xs={12}>
                  <Tooltip placement="top" title="destination delivery address">
                    <TextField
                      error={Boolean(formik.touched.ship_to_address1 && formik.errors.ship_to_address1)}
                      fullWidth
                      helperText={formik.touched.ship_to_address1 && formik.errors.ship_to_address1}
                      label="Delivery Address Line 1"
                      name="ship_to_address1"
                      id="ship_to_address1"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.ship_to_address1}
                      variant="outlined"
                      required
                      multiline
                      inputProps={{ maxLength: 40 }}
                    />
                  </Tooltip>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Tooltip placement="top" title="additional delivery notes. *may not be shown on label">
                    <TextField
                      fullWidth
                      label="Delivery Attention to"
                      name="special_details_3"
                      id="special_details_3"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.special_details_3}
                      variant="outlined"
                      inputProps={{ maxLength: 40 }}
                    />
                  </Tooltip>
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Address Line 2"
                    name="ship_to_address2"
                    id="ship_to_address2"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.ship_to_address2}
                    variant="outlined"
                    multiline
                    inputProps={{ maxLength: 40 }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Ship to Code"
                    name="ship_to_code"
                    id="ship_to_code"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.ship_to_code}
                    variant="outlined"
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextField
                    error={Boolean(formik.touched.ship_to_city && formik.errors.ship_to_city)}
                    fullWidth
                    helperText={formik.touched.ship_to_city && formik.errors.ship_to_city}
                    label="Delivery Suburb"
                    name="ship_to_city"
                    id="ship_to_city"
                    required
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.ship_to_city}
                    variant="outlined"
                    inputProps={{ maxLength: 30 }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Ship to Fax Num"
                    name="special_details_2"
                    id="special_details_2"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.special_details_2}
                    variant="outlined"
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    error={Boolean(formik.touched.ship_to_state && formik.errors.ship_to_state)}
                    required
                    helperText={formik.touched.ship_to_state && formik.errors.ship_to_state}
                    select={formik.values.ship_to_country == "AU"}
                    label="Delivery State"
                    name="ship_to_state"
                    id="ship_to_state"
                    onChange={formik.handleChange}
                    value={formik.values.ship_to_state}
                    variant="outlined"
                    inputProps={{ maxLength: 25 }}
                  >
                    {formik.values.ship_to_country == "AU" &&
                      ausStates.map(option => (
                        <MenuItem id={`ship_to_state-${option}`} key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Tooltip placement="top" title="business to consumer or business to business shipment">
                    <FormControl>
                      <FormLabel>Sales Channel</FormLabel>
                      <RadioGroup row aria-labelledby="sales_channel" name="sales_channel" id="sales_channel" value={formik.values.sales_channel} onChange={formik.handleChange}>
                        <FormControlLabel id="sales_channel_None" value="" control={<Radio />} label="None" />
                        <FormControlLabel id="sales_channel_B2B" value="B2B" control={<Radio />} label="B2B" />
                        <FormControlLabel id="sales_channel_B2C" value="B2C" control={<Radio />} label="B2C" />
                      </RadioGroup>
                    </FormControl>
                  </Tooltip>
                </Grid>

                <Grid item md={6} xs={12}>
                  <Autocomplete
                    disableClearable
                    fullWidth
                    name="ship_to_country"
                    id="ship_to_country"
                    value={rootStore.getTextByValue("COUNTRY", formik.values.ship_to_country)}
                    onChange={(event, item) => handleDeliveryCountryChange(item.id)}
                    options={countries.map(option => ({ label: `${option.value} - ${option.label}`, id: option.value }))}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Delivery Country"
                        required
                        error={Boolean(formik.touched.ship_to_country && formik.errors.ship_to_country)}
                        helperText={formik.touched.ship_to_country && formik.errors.ship_to_country}
                      />
                    )}
                    required
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Tooltip placement="top" title="destination contact phone number">
                    <TextField
                      fullWidth
                      label="Delivery Phone Number"
                      name="ship_to_phone_num"
                      id="ship_to_phone_num"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.ship_to_phone_num}
                      variant="outlined"
                      inputProps={{ maxLength: 40 }}
                    />
                  </Tooltip>
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    error={Boolean(formik.touched.ship_to_postal_code && formik.errors.ship_to_postal_code)}
                    helperText={formik.touched.ship_to_postal_code && formik.errors.ship_to_postal_code}
                    label="Delivery Postal Code"
                    name="ship_to_postal_code"
                    id="ship_to_postal_code"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    required
                    value={formik.values.ship_to_postal_code}
                    variant="outlined"
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Tooltip placement="top" title="destination contact email address">
                    <TextField
                      fullWidth
                      label="Delivery Email Address"
                      name="ship_to_email_address"
                      id="ship_to_email_address"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.ship_to_email_address}
                      variant="outlined"
                      type="email"
                      inputProps={{ maxLength: 40 }}
                      error={Boolean(formik.touched.ship_to_email_address && formik.errors.ship_to_email_address)}
                      helperText={formik.touched.ship_to_email_address && formik.errors.ship_to_email_address}
                    />
                  </Tooltip>
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Instructions"
                    name="delivery_instructions"
                    id="delivery_instructions"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.delivery_instructions}
                    variant="outlined"
                    inputProps={{ maxLength: 25 }}
                  />
                </Grid>
                {formik.values.ship_to_country !== "AU" && (
                  <>
                    <Grid item md={6} xs={12}>
                      <Tooltip placement="top" title="Who is responsible for duties on import">
                        <TextField fullWidth select label="Incoterm" name="incoterm" id="incoterm" onChange={formik.handleChange} value={formik.values.incoterm || ""} variant="outlined">
                          {rootStore.getOptionsByFilter("INCOTERMS").map(option => (
                            <MenuItem id={`incoterm-${option.value}`} key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Tooltip>
                    </Grid>
                  </>
                )}
              </GridContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader title="Customer information" />
            <CardContent>
              <GridContainer container spacing={3}>
                {showCustomerInfo && (
                  <>
                    <Grid item md={6} xs={12}>
                      <Tooltip placement="top" title="store contact name *for EDI orders">
                        <TextField
                          fullWidth
                          label="Customer Name"
                          name="customer_name"
                          id="customer_name"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.customer_name}
                          variant="outlined"
                          inputProps={{ maxLength: 40 }}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Autocomplete
                        disableClearable
                        fullWidth
                        name="customer_country"
                        id="customer_country"
                        value={rootStore.getTextByValue("COUNTRY", formik.values.customer_country)}
                        onChange={(event, item) => formik.setFieldValue("customer_country", item.id)}
                        options={countries.map(option => ({ label: `${option.value} - ${option.label}`, id: option.value }))}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Customer Country"
                            error={Boolean(formik.touched.customer_country && formik.errors.customer_country)}
                            helperText={formik.touched.customer_country && formik.errors.customer_country}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Company Name"
                        name="customer"
                        id="customer"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.customer}
                        variant="outlined"
                        inputProps={{ maxLength: 25 }}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Address Line 1"
                        name="customer_address1"
                        id="customer_address1"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.customer_address1}
                        variant="outlined"
                        inputProps={{ maxLength: 40 }}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Phone Number"
                        name="customer_phone_num"
                        id="customer_phone_num"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.customer_phone_num}
                        variant="outlined"
                        inputProps={{ maxLength: 40 }}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Address Line 2"
                        name="customer_address2"
                        id="customer_address2"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.customer_address2}
                        variant="outlined"
                        inputProps={{ maxLength: 40 }}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Email Address"
                        name="customer_email_address"
                        id="customer_email_address"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.customer_email_address}
                        variant="outlined"
                        type="email"
                        inputProps={{ maxLength: 40 }}
                        error={Boolean(formik.touched.customer_email_address && formik.errors.customer_email_address)}
                        helperText={formik.touched.customer_email_address && formik.errors.customer_email_address}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Suburb"
                        name="customer_city"
                        id="customer_city"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.customer_city}
                        variant="outlined"
                        inputProps={{ maxLength: 30 }}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Code"
                        name="customer_code"
                        id="customer_code"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.customer_code}
                        variant="outlined"
                        inputProps={{ maxLength: 4 }}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <TextField
                        fullWidth
                        select={formik.values.customer_country == "AU"}
                        inputProps={{ maxLength: 25 }}
                        label="Customer State"
                        name="customer_state"
                        id="customer_state"
                        onChange={formik.handleChange}
                        value={formik.values.customer_state}
                        variant="outlined"
                      >
                        {formik.values.customer_country == "AU" &&
                          ausStates.map(option => (
                            <MenuItem id={`customer_state-${option}`} key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Postal Code"
                        name="customer_postal_code"
                        id="customer_postal_code"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.customer_postal_code}
                        variant="outlined"
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                  </>
                )}
              </GridContainer>
            </CardContent>
          </Card>
        </Stack>
      </form>
    )
  }),
)

export default SaleOrderDelivery
